'use client';
import React, { useCallback } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { format, parse } from 'date-fns';

import { Box } from '@nerdwallet/currency/components/Box';
import { Input } from '@nerdwallet/currency/components/Input';
import { InputDate } from '@nerdwallet/currency/components/InputDate';
import { InputPhone } from '@nerdwallet/currency/components/InputPhone';
import { InputZIP } from '@nerdwallet/currency/components/InputZIP';
import { SecureEncryptionMessage } from '@nerdwallet/currency/components/SecureEncryptionMessage';
import { Select } from '@nerdwallet/currency/components/Select';
import GoogleMapsAutocomplete from '@nerdwallet/react-google-maps-autocomplete';

import { SegmentPageView } from '@lib/segment';
import { zodResolver } from '@hookform/resolvers/zod';
import { parseGoogleAddress } from '@lib/utils/google-autocomplete-utils';
import ContinueButton from '../components/continue-button';
import FriendlyTitle from '../components/friendly-title';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import {
  TuStep,
  useTuStepper,
} from '@components/transunion-flow/tu-modal-stepper';
import { stateAbbreviations } from '@constants/common';
import { BasicInfoFormSchema, schema } from './basic-info-form-schema';
import { CreditProfileAuthCustomerInput } from '@generated-gql/generated-types';
import styles from './basic-info.module.scss';

const getDefaultPhone = (phone?: string): string | undefined => {
  if (phone?.length === 11) {
    // strip out the initial digit
    return phone.slice(1);
  }
  return phone;
};

const BasicInfoStep = () => {
  const { setTuVisibility } = useTuVisibilityContext();
  const { setTuCustomerInfo } = useTuStepper();

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    getValues: getFormValues,
    setValue,
    clearErrors,
    control,
  } = useFormContext<BasicInfoFormSchema>();

  const onSubmitForm = () => {
    const formValues = getFormValues();
    const cleanedPhoneNumber = formValues.phoneNumber.replace(/\D/g, '');
    formValues.phoneNumber = `${cleanedPhoneNumber.length === 10 ? `1` : ``}${cleanedPhoneNumber}`;
    formValues.dateOfBirth = format(
      parse(formValues.dateOfBirth, 'MM / dd / yyyy', new Date()),
      'yyyy-MM-dd',
    );

    setTuCustomerInfo(formValues as CreditProfileAuthCustomerInput);
    setTuVisibility({
      show: true,
      tuStep: TuStep.TU_SHORT_SSN,
    });
  };

  const handleAddressSelect = useCallback(
    (selectedPlace?: google.maps.places.PlaceResult) => {
      const googleAddress = parseGoogleAddress(selectedPlace);

      setValue(
        'mailingAddress.addressLine1',
        `${googleAddress.streetNumber} ${googleAddress.streetName}`,
      );
      setValue('mailingAddress.addressLine2', '');
      setValue('mailingAddress.city', googleAddress.city);
      setValue('mailingAddress.state', googleAddress.state);
      setValue('mailingAddress.zipCode', googleAddress.zipCode, {
        shouldValidate: true,
      });

      clearErrors('mailingAddress');
    },
    [setValue, clearErrors],
  );

  return (
    <SegmentPageView name="credit_score_basic_info">
      <Box display="flex" flexDirection="column" width={100}>
        <FriendlyTitle
          title={"You're just a few steps away from your credit score."}
          subtext={
            "And remember, checking your credit score won't impact it at all."
          }
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(onSubmitForm)(e);
          }}
        >
          <Box display="flex" gridGap="2">
            <Box width="50">
              <Input
                {...register('firstName')}
                label="Legal first name"
                autoComplete={'given-name'}
                error={!!errors?.firstName}
                errorMessage={errors?.firstName?.message}
                fullWidth
              />
            </Box>
            <Box width="50">
              <Input
                {...register('lastName')}
                label="Legal last name"
                autoComplete={'family-name'}
                error={!!errors?.lastName}
                errorMessage={errors?.lastName?.message}
                fullWidth
              />
            </Box>
          </Box>
          <Box display="flex" marginTop={2} gridGap="2">
            <Box width="50">
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field }) => (
                  <InputDate
                    {...field}
                    name="dateOfBirth"
                    label="Birthday"
                    error={!!errors?.dateOfBirth}
                    errorMessage={errors?.dateOfBirth?.message}
                    fullWidth
                  />
                )}
              />
            </Box>
            <Box width="50">
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <InputPhone
                    {...field}
                    name="phoneNumber"
                    label="Phone Number"
                    placeholder="+1"
                    error={!!errors?.phoneNumber}
                    errorMessage={errors?.phoneNumber?.message}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Box>

          <Box marginTop={2}>
            <Controller
              control={control}
              name="mailingAddress.addressLine1"
              render={({ field: { onChange, value, onBlur } }) => (
                <GoogleMapsAutocomplete
                  name="mailingAddress.addressLine1"
                  label="Street address"
                  error={!!errors?.mailingAddress?.addressLine1}
                  errorMessage={errors?.mailingAddress?.addressLine1?.message}
                  onSelect={handleAddressSelect}
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <Box marginTop={2}>
              <Input
                {...register('mailingAddress.addressLine2')}
                label="Address line 2 (optional)"
                error={!!errors.mailingAddress?.addressLine2}
                errorMessage={errors.mailingAddress?.addressLine2?.message}
                fullWidth
              />
            </Box>
            <Box display="flex" marginTop={2} gridGap="2">
              <div className={styles.zipCodeStateColumn}>
                <Input
                  {...register('mailingAddress.city')}
                  label="City"
                  error={!!errors.mailingAddress?.city}
                  errorMessage={errors.mailingAddress?.city?.message}
                  fullWidth
                />
              </div>
              <div className={styles.zipCodeStateColumn}>
                <Controller
                  name="mailingAddress.state"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      name="mailingAddress.state"
                      label="State"
                      error={!!errors?.mailingAddress?.state}
                      errorMessage={errors?.mailingAddress?.state?.message}
                      fullWidth
                      options={stateAbbreviations.map((stateAbbreviation) => ({
                        label: stateAbbreviation,
                        value: stateAbbreviation,
                      }))}
                    />
                  )}
                />
              </div>
            </Box>
            <Box marginTop={2}>
              <Controller
                name="mailingAddress.zipCode"
                control={control}
                rules={{ required: 'Zip code is required' }}
                render={({ field: { onChange, value, onBlur } }) => (
                  <InputZIP
                    name="mailingAddress.zipCode"
                    label="Zip code"
                    error={!!errors?.mailingAddress?.zipCode}
                    errorMessage={errors?.mailingAddress?.zipCode?.message}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </Box>
          </Box>

          <Box marginTop={4}>
            <ContinueButton isDisabled={!isValid} />
          </Box>

          <Box marginTop={4}>
            <SecureEncryptionMessage />
          </Box>
        </form>
      </Box>
    </SegmentPageView>
  );
};

const BasicInfoStepContainer = () => {
  const { tuCustomerInfo } = useTuStepper();
  const formMethods = useForm<BasicInfoFormSchema>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: tuCustomerInfo?.firstName,
      lastName: tuCustomerInfo?.lastName,
      dateOfBirth: tuCustomerInfo?.dateOfBirth
        ? format(
            parse(tuCustomerInfo?.dateOfBirth, 'yyyy-MM-dd', new Date()),
            'MM / dd / yyyy',
          )
        : null,
      phoneNumber: getDefaultPhone(tuCustomerInfo?.phoneNumber),
      mailingAddress: tuCustomerInfo?.mailingAddress,
    },
  });

  return (
    <FormProvider {...formMethods}>
      <BasicInfoStep />
    </FormProvider>
  );
};

export default BasicInfoStepContainer;
